// utils/v3/hash.ts — deterministic content hashing (SPEC §6)
// Used to derive entity "versions" for data sources that do not publish one.

/** JSON.stringify with object keys sorted recursively (stable across key order). */
export function stableStringify(value: unknown): string {
  // JSON.stringify returns undefined for undefined / functions — normalise to a string.
  return JSON.stringify(normalize(value)) ?? 'undefined'
}

function normalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalize)
  if (value && typeof value === 'object') {
    const src = value as Record<string, unknown>
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(src).sort()) out[key] = normalize(src[key])
    return out
  }
  return value
}

const FNV_OFFSET_A = 0x811c9dc5
const FNV_OFFSET_B = 0x01000193

function fnv1a32(input: string, seed: number): number {
  let h = seed >>> 0
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i) & 0xff
    // h *= 16777619 (FNV prime) in 32-bit space
    h = Math.imul(h, 0x01000193) >>> 0
    // fold the high byte of the code unit so non-ASCII text still contributes
    const hi = input.charCodeAt(i) >>> 8
    if (hi) {
      h ^= hi
      h = Math.imul(h, 0x01000193) >>> 0
    }
  }
  return h >>> 0
}

const hex8 = (n: number): string => (n >>> 0).toString(16).padStart(8, '0')

/** FNV-1a 64-bit (two 32-bit halves) over stableStringify(value), hex. */
export function hashJson(value: unknown): string {
  const s = stableStringify(value)
  return hex8(fnv1a32(s, FNV_OFFSET_A)) + hex8(fnv1a32(s, FNV_OFFSET_B))
}
